import React from 'react';
import { Layout } from '../components/Layout';

export function Terms() {
  return (
    <Layout>
      {/* Header */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <h1 className="text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-xl text-blue-100">
            Agreement governing your use of AOK Financial services
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <p className="lead">
              <strong>Effective Date:</strong> January 1, 2026
            </p>

            <p>
              These Terms of Service ("Terms") govern your use of the trading services and API provided by AOK Financial Inc. ("AOK Financial," "we," "us," or "our"). By using our services, you agree to be bound by these Terms.
            </p>

            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use our services.
            </p>

            <h2>2. Eligibility</h2>
            <p>
              To use our services, you must:
            </p>
            <ul>
              <li>Be at least 18 years of age</li>
              <li>Be authorized to enter into binding contracts</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not be located in a jurisdiction where our services are prohibited</li>
            </ul>

            <h2>3. Account Registration</h2>
            <p>
              To access our services, you must register for an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information to keep it accurate and complete</li>
              <li>Maintain the security and confidentiality of your API keys and credentials</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
            </ul>

            <h2>4. Trading Services</h2>
            <h3>4.1 Scope of Services</h3>
            <p>
              AOK Financial provides introducing broker-dealer services for US equity trading, including:
            </p>
            <ul>
              <li>Order routing and execution</li>
              <li>Extended hours and 24/5 trading</li>
              <li>Fractional share trading</li>
              <li>API access for programmatic trading</li>
            </ul>

            <h3>4.2 Clearing Services</h3>
            <p>
              Trade clearing and settlement are provided by our clearing partner, RQD. You acknowledge that:
            </p>
            <ul>
              <li>Your securities are held in custody by RQD</li>
              <li>You are subject to RQD's customer agreement and terms</li>
              <li>SIPC protection applies to your account as provided through RQD</li>
            </ul>

            <h3>4.3 Risks</h3>
            <p>
              Trading securities involves substantial risk of loss. You acknowledge and accept:
            </p>
            <ul>
              <li>You may lose your entire investment</li>
              <li>Past performance does not guarantee future results</li>
              <li>Extended hours trading carries additional risks (lower liquidity, higher volatility)</li>
              <li>Fractional shares may have limited liquidity</li>
            </ul>

            <h2>5. API Usage</h2>
            <h3>5.1 API Keys</h3>
            <p>
              You are responsible for:
            </p>
            <ul>
              <li>Keeping your API keys confidential</li>
              <li>Implementing appropriate security measures</li>
              <li>Monitoring usage and detecting unauthorized access</li>
              <li>Immediately revoking compromised keys</li>
            </ul>

            <h3>5.2 Rate Limits and Usage</h3>
            <p>
              We may impose rate limits on API usage to ensure service quality. Excessive or abusive API usage may result in temporary or permanent suspension of access.
            </p>

            <h3>5.3 Prohibited Activities</h3>
            <p>
              You may not:
            </p>
            <ul>
              <li>Use our services for any illegal or unauthorized purpose</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Reverse engineer or decompile our software or APIs</li>
              <li>Interfere with or disrupt our services or servers</li>
              <li>Transmit viruses, malware, or harmful code</li>
              <li>Manipulate markets or engage in fraudulent trading practices</li>
            </ul>

            <h2>6. Fees and Charges</h2>
            <p>
              While we do not charge commissions on trades, you acknowledge that:
            </p>
            <ul>
              <li>Regulatory fees (SEC, FINRA) apply to transactions</li>
              <li>Third-party fees may apply (wire transfer fees, etc.)</li>
              <li>We reserve the right to introduce fees with 30 days' notice</li>
            </ul>

            <h2>7. Intellectual Property</h2>
            <p>
              All content, software, and materials provided through our services are owned by AOK Financial or our licensors. You may not:
            </p>
            <ul>
              <li>Copy, modify, or create derivative works</li>
              <li>Distribute, sell, or sublicense our content</li>
              <li>Remove or modify proprietary notices</li>
            </ul>

            <h2>8. Disclaimers</h2>
            <p>
              <strong>NO INVESTMENT ADVICE:</strong> We do not provide investment advice or recommendations. All trading decisions are your sole responsibility.
            </p>

            <p>
              <strong>NO WARRANTIES:</strong> Our services are provided "AS IS" without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
            </p>

            <p>
              <strong>SYSTEM AVAILABILITY:</strong> While we strive for high availability, we do not guarantee uninterrupted access to our services. We may perform maintenance or experience outages that affect service availability.
            </p>

            <h2>9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, AOK FINANCIAL SHALL NOT BE LIABLE FOR:
            </p>
            <ul>
              <li>Indirect, incidental, special, consequential, or punitive damages</li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>Trading losses or investment losses</li>
              <li>System downtime or service interruptions</li>
              <li>Errors, inaccuracies, or delays in data or executions</li>
            </ul>

            <p>
              Our total liability to you for any claims arising from these Terms or your use of our services shall not exceed $1,000.
            </p>

            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless AOK Financial, its affiliates, and their respective officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses arising from:
            </p>
            <ul>
              <li>Your use of our services</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any law or regulation</li>
              <li>Your trading activities</li>
            </ul>

            <h2>11. Termination</h2>
            <p>
              We may suspend or terminate your access to our services at any time, with or without cause or notice, for any reason including:
            </p>
            <ul>
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Regulatory requirements</li>
              <li>Risk management concerns</li>
            </ul>

            <p>
              Upon termination, you must cease all use of our services and API. Provisions regarding liability, indemnification, and dispute resolution survive termination.
            </p>

            <h2>12. Dispute Resolution</h2>
            <h3>12.1 Governing Law</h3>
            <p>
              These Terms are governed by the laws of the State of Delaware, without regard to conflict of law principles.
            </p>

            <h3>12.2 Arbitration</h3>
            <p>
              Any disputes arising from these Terms shall be resolved through binding arbitration in accordance with FINRA's Code of Arbitration Procedure, except for claims that may be brought in small claims court.
            </p>

            <h2>13. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website with a new effective date. Your continued use of our services after changes constitutes acceptance of the modified Terms.
            </p>

            <h2>14. Miscellaneous</h2>
            <p>
              <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, constitute the entire agreement between you and AOK Financial.
            </p>

            <p>
              <strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions remain in full effect.
            </p>

            <p>
              <strong>No Waiver:</strong> Our failure to enforce any right or provision does not constitute a waiver of that right or provision.
            </p>

            <p>
              <strong>Assignment:</strong> You may not assign these Terms without our written consent. We may assign these Terms without restriction.
            </p>

            <h2>15. Contact Information</h2>
            <p>
              For questions about these Terms, please contact us:
            </p>
            <p>
              <strong>AOK Financial Inc.</strong><br />
              Email: <a href="mailto:legal@aokfin.com" className="text-accent-600 hover:text-accent-700">legal@aokfin.com</a><br />
              General Inquiries: <a href="mailto:info@aokfin.com" className="text-accent-600 hover:text-accent-700">info@aokfin.com</a>
            </p>

            <div className="mt-8 text-sm text-gray-600">
              <p>
                <em>Last Updated: January 1, 2026</em>
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
